import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Image } from "@/components/ui/image";
import StayPhotoFallback from "@/components/stays/StayPhotoFallback";
import { PROPERTY_TYPE_ES, coverOf } from "@/lib/realestate";
import { propertyWebsite } from "@/lib/stays";
import { base44 } from "@/api/base44Client";

/** Curated home-feed stay: strong photography first, discovery instead of booking widgets. */
export default function StayCard({ listing, es }) {
  const image = coverOf(listing);
  const [imageFailed, setImageFailed] = useState(false);
  const url = propertyWebsite(listing);
  const trackClick = () => {
    try {
      Promise.resolve(base44.analytics.track({
        eventName: "stay_outbound_click",
        properties: { hotel_id: listing.id, partner: "home-rail" },
      })).catch(() => {});
    } catch { /* Navigation remains available even if analytics fails. */ }
  };

  return (
    <article className="group flex h-full flex-col">
      <Link to={`/hotels/${listing.id}`} className="flex h-full flex-col rounded-[9px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ef4036]/60 focus-visible:ring-offset-2">
        <div className="relative aspect-square w-full overflow-hidden rounded-[9px] bg-[#EDEBE6] ring-1 ring-black/[0.06]">
          {image && !imageFailed ? (
            <Image src={image} alt={listing.title} fittingType="fill" quality={80} loading="lazy" onError={() => setImageFailed(true)} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.035]" />
          ) : <StayPhotoFallback label={es ? "Sin foto" : "No photo"} />}
          <span className="absolute bottom-2.5 left-2.5 max-w-[90%] truncate rounded-full bg-white/95 px-2.5 py-1 text-[9px] font-bold text-[#171717] shadow-sm">{listing.area}</span>
          {listing.featured && <span className="absolute left-2.5 top-2.5 rounded-full bg-[#171717] px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] text-white">{es ? "Destacado" : "Featured"}</span>}
        </div>
        <div className="flex flex-1 flex-col pt-3">
          <p className="text-[9px] font-bold uppercase tracking-[0.13em] text-[#8d8d88]">{(es && PROPERTY_TYPE_ES[listing.property_type]) || listing.property_type || (es ? "Hospedaje" : "Stay")}</p>
          <h3 className="mt-1 line-clamp-2 min-h-[2.5em] break-words text-[14px] font-extrabold leading-[1.25] text-[#24231f]">{listing.title}</h3>
          <span className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-[#77736c]"><MapPin aria-hidden="true" className="h-3.5 w-3.5" /><span className="truncate">{listing.area || "Puerto Rico"}</span></span>
          <span className="mt-auto pt-3 text-[11px] font-bold text-[#171717] underline-offset-4 group-hover:underline">{es ? "Conoce este lugar" : "Explore this stay"} <ArrowUpRight aria-hidden="true" className="inline h-3.5 w-3.5" /></span>
        </div>
      </Link>
      {url && <a href={url} target="_blank" rel="noopener noreferrer" onClick={trackClick} className="mt-3 inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-full border border-black/[0.13] bg-white px-3 text-[11px] font-bold text-[#171717] transition hover:border-[#171717] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ef4036] focus-visible:ring-offset-2">
        {es ? "Sitio del hotel" : "Property website"} <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
      </a>}
    </article>
  );
}
